import { useNavigate } from "react-router-dom";
import { useAdminMembers, useAdminLoans, useReportFinancial } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import MiniBarChart from "../../components/charts/MiniBarChart";
import DonutChart from "../../components/charts/DonutChart";

const fmt = (n: number) => new Intl.NumberFormat("en-NG",{style:"currency",currency:"NGN", currencyDisplay:"symbol",maximumFractionDigits:0}).format(n);

export default function OverviewPage() {
  const navigate     = useNavigate();
  const { data: adminmembers = [] } = useAdminMembers() as any;
  const { data: adminloans   = [] } = useAdminLoans()   as any;
  const { data: fin }          = useReportFinancial() as any;

  const pendingKyc   = adminmembers.filter((m: any) => m.kyc_status === "reviewing").length;
  const overdueLoans = adminloans.filter((l: any) => l.status === "overdue").length;
  const {savings={}, loans={}, members={}} = fin || 0;

  const donutSegments = [
    { value: adminloans.filter((l:any)=>l.status==="active").length,         color:"#10b981", label:"Active"  },
    { value: adminloans.filter((l:any)=>l.status==="repaid").length,         color:"#3b82f6", label:"Repaid"  },
    { value: adminloans.filter((l:any)=>l.status==="overdue").length,        color:"#ef4444", label:"Overdue" },
    { value: adminloans.filter((l:any)=>l.status==="pending-surety").length, color:"#f59e0b", label:"Pending" },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Total Members"    value={adminmembers.length}                           sub={`${adminmembers.filter((m:any)=>m.status==="active").length} active`} color="text-emerald-600" icon="👥"/>
        <StatCard label="Funds Under Mgmt" value={fmt(savings?.[0]?.total_savings||0)}           sub="Member balances"   color="text-blue-600"   icon="💰"/>
        <StatCard label="Loan Disbursed"  value={fmt(loans?.[0]?.total_disbursed||0)}           sub="All loans"         color="text-violet-600" icon="🤝"/>
        <StatCard label="Recovery Rate"    value={`${fin ? Math.round(((loans?.[0]?.total_repaid||0)/Math.max((loans?.[0]?.total_disbursed||1),1))*100) : 0}%`} sub="Repaid vs disbursed" color="text-emerald-600" icon="✅"/>
        <StatCard label="Pending KYC"      value={pendingKyc}                                    sub="Awaiting review"   color="text-amber-600"  icon="🛡️"/>
        <StatCard label="Overdue Loans"    value={overdueLoans}                                  sub="Needs action"      color="text-red-600"    icon="⚠️"/>
      </div>
    
      {(pendingKyc > 0 || overdueLoans > 0) && (
        <Card className="mb-5 border-amber-400 bg-amber-50">
          <p className="text-sm font-medium text-amber-800 mb-3">⚠️ Action required</p>
          <div className="flex gap-2 flex-wrap">
            {pendingKyc   > 0 && <Button variant="amber" size="sm" onClick={() => navigate("/admin/members?kyc=reviewing")}>{pendingKyc} KYC review{pendingKyc>1?"s":""} pending</Button>}
            {overdueLoans > 0 && <Button variant="danger" size="sm" onClick={() => navigate("/admin/loans?status=overdue")}>{overdueLoans} overdue loan{overdueLoans>1?"s":""}</Button>}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5">
        <Card className="lg:col-span-3">
          <p className="text-sm font-medium text-slate-700 mb-1">Monthly overview</p>
          <div className="flex gap-4 mb-3 flex-wrap">
            {[{c:"#10b981",l:"Contributions"},{c:"#3b82f6",l:"Disbursements"},{c:"#8b5cf6",l:"Repayments"}].map(leg=>(
              <div key={leg.l} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{background:leg.c}}/>
                <span className="text-xs text-slate-500">{leg.l}</span>
              </div>
            ))}
          </div>
          <MiniBarChart
            data={[
              {month:"Jan",contributions:42000,disbursements:13000,repayments:4000},
              {month:"Feb",contributions:47500,disbursements:8000, repayments:5000},
              {month:"Mar",contributions:51000,disbursements:11000,repayments:6500},
              {month:"Apr",contributions:55500,disbursements:5000, repayments:7000},
              {month:"May",contributions:61200,disbursements:16000,repayments:8000},
            ]}
            keys={["contributions","disbursements","repayments"]}
            colors={["#10b981","#3b82f6","#8b5cf6"]}
            height={150}
          />
        </Card>
        <Card className="lg:col-span-2">
          <p className="text-sm font-medium text-slate-700 mb-4">Loan portfolio</p>
          <div className="flex gap-5 items-center">
            <div className="relative flex-shrink-0">
              <DonutChart segments={donutSegments} size={110} stroke={18}/>
            </div>
            <div className="flex-1 space-y-2.5">
              {[
                {label:"Active",  count:adminloans.filter((l:any)=>l.status==="active").length,         color:"#10b981"},
                {label:"Repaid",  count:adminloans.filter((l:any)=>l.status==="repaid").length,          color:"#3b82f6"},
                {label:"Overdue", count:adminloans.filter((l:any)=>l.status==="overdue").length,         color:"#ef4444"},
                {label:"Pending", count:adminloans.filter((l:any)=>l.status==="pending-surety").length,  color:"#f59e0b"},
              ].map(s=>(
                <div key={s.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{background:s.color}}/>
                    <span className="text-sm text-slate-600">{s.label}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-800">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
